import React, { useRef, DragEvent, ChangeEvent } from "react";
import axios from "axios";
import { Chip } from "@mui/material";
import { global } from "@shared/libs/Global";
import SnButton from "@shared/components/SnButton";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import SN_SFA_ISSUE_REG_002_POP_006 from "@menu/SFA/SN_SFA_ISSUE_REG_002/SN_SFA_ISSUE_REG_002_POP_DLL_HIS";

/**
 * 제품구분(fg_item) 코드 → 서버 DLL 폴더명.
 * 윈폼 M_SFA_ISS_REG_002 의 switch(fg_item) 과 동일. 서버 경로: HttpFile\Upload\{firm}\DLL\{폴더}\
 */
export const getDllFolder = (fg_item: string): string => {
  switch (String(fg_item ?? "")) {
    case "10":
      return "FR";
    case "20":
      return "AIMS";
    case "21":
    case "22":
      return "SNOTES";
    case "31":
      return "DOTNET";
    case "32":
      return "SN_POP";
    case "35":
      return "MOBILE";
    case "40":
      return "FRAMEWORK";
    case "50":
      return "ETC";
    default:
      return String(fg_item ?? "");
  }
};

/** 저장 대상: 저장 창에서 고른 파일 / 브라우저 다운로드 폴더(미지원 브라우저) */
export type SaveTarget = { kind: "file"; handle: any } | { kind: "download" };

/**
 * (Chip 단건 클릭용) 저장 창을 먼저 띄워 위치를 고르게 한다. Chrome/Edge 의 File System Access API.
 * 서버 호출보다 먼저 호출해야 한다. 클릭 뒤 시간이 지나면 브라우저가 창 열기를 거부한다.
 * 미지원 브라우저(Firefox, Safari)나 http 환경이면 "download" (브라우저 다운로드 폴더)
 * @returns SaveTarget | "cancel" (사용자가 창을 닫음)
 */
export const pickSaveTarget = async (
  fileName: string
): Promise<SaveTarget | "cancel"> => {
  const w = window as any;
  if (typeof w.showSaveFilePicker !== "function") return { kind: "download" };
  try {
    const ext = fileName.includes(".")
      ? fileName.substring(fileName.lastIndexOf("."))
      : "";
    const handle = await w.showSaveFilePicker({
      suggestedName: fileName,
      types: ext
        ? [{ description: ext, accept: { "application/octet-stream": [ext] } }]
        : undefined,
    });
    return { kind: "file", handle };
  } catch (err: any) {
    if (err?.name === "AbortError") return "cancel";
    throw err;
  }
};

/** 고른 위치에 파일을 쓴다 */
export const writeBlob = async (
  target: SaveTarget,
  fileName: string,
  blob: Blob
) => {
  if (target.kind === "file") {
    const writable = await target.handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  saveToDownloads(blob, fileName);
};

/** 받은 blob 을 브라우저 다운로드 폴더에 떨어뜨린다 */
const saveToDownloads = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** 서버 오류 메시지. axios 응답이 blob 이라 상태코드만 보여준다 */
export const downloadErrorMessage = (prefix: string, err: any) => {
  const status = err?.response?.status;
  if (status) return `${prefix} (HTTP ${status})`;
  return err?.message ? `${prefix}\n${err.message}` : prefix;
};

/**
 * 이슈의 DLL 첨부를 전부 브라우저 다운로드 폴더에 파일별로 내려받는다. (윈폼 Apply Test 의 FN_Download 대응:
 * 윈폼은 TempDownload\{no_issue} 폴더로 받아 탐색기를 연다)
 * Chrome 은 폴더 핸들(File System Access API)로 .dll 생성을 막아서 "폴더 골라서 바로 쓰기"는 불가능하다.
 * 두 번째 파일부터 브라우저가 "여러 파일 다운로드 허용" 을 한 번 물을 수 있다.
 * @returns 성공 여부
 */
export const downloadDllFiles = async (
  _no_issue: string,
  fg_item: string,
  files: string[]
): Promise<boolean> => {
  const names = files.filter((f) => !!f);
  if (names.length === 0) {
    await showDialog("alter", "내려받을 DLL 파일명이 없습니다.");
    return false;
  }

  const headers = {
    "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
  };
  const folder = getDllFolder(fg_item);

  try {
    for (const nm_file of names) {
      const res = await axios.post(
        "/snotes/executeScript/downloadDllDotNet",
        { fg_item: folder, nm_file },
        { headers, responseType: "blob" }
      );
      saveToDownloads(new Blob([res.data]), nm_file);
    }
    return true;
  } catch (err: any) {
    await showDialog(
      "error",
      downloadErrorMessage("DLL 다운로드를 실패했습니다.", err)
    );
    return false;
  }
};

/**
 * MSSQL 제품(fg_item) 이슈의 DLL/XML 첨부 박스.
 * 서버(Java)에는 fg_item 코드가 아니라 getDllFolder() 로 변환한 폴더명을 fg_item 키로 보낸다.
 */
interface Props {
  no_issue: string;
  fg_item: string;
  dllList: any[];
  /** 이슈 상태. 5(완료)·9(취소)면 첨부 추가/삭제 불가 (다운로드·이력은 허용) */
  st_issue?: string;
  /** 업로드 성공 후 목록 재조회용 콜백 */
  onUploaded?: () => void | Promise<void>;
  /** 삭제 성공 후 목록 재조회용 콜백 */
  onDeleted?: () => void | Promise<void>;
}

/** 완료(5)·취소(9) 이슈는 첨부를 바꿀 수 없다 */
const isAttachLocked = (st_issue?: string) =>
  st_issue === "5" || st_issue === "9";
const LOCKED_MESSAGE = "완료 또는 취소된 이슈는 첨부를 변경할 수 없습니다.";

const SN_SFA_ISSUE_REG_002_DLL_BOX = ({
  no_issue,
  fg_item,
  dllList,
  st_issue,
  onUploaded,
  onDeleted,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // 서버 폴더명 (31 → DOTNET). 업로드/다운로드/삭제 모두 이 값을 fg_item 으로 보낸다
  const dllFolder = getDllFolder(fg_item);
  const locked = isAttachLocked(st_issue);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => e.preventDefault();

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0)
      await uploadFiles(e.dataTransfer.files);
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await uploadFiles(e.target.files);
    }
    // 같은 파일을 다시 선택해도 onChange가 뜨도록 초기화
    e.target.value = "";
  };

  // DC_URL(http://{server}/DLL/{fg_item}/{file})은 직접 열리는 경로가 아니다(IIS가 .dll 을 실행하려다 500).
  // 윈폼 HttpFileHelper.Download 와 같이 .NET /WebAPI/File/DownloadFile 을 Spring 중계로 호출한다.
  const handleDownload = async (file: any) => {
    try {
      // 저장 위치를 먼저 고른다 (미지원 브라우저는 다운로드 폴더)
      const target = await pickSaveTarget(file.nm_file);
      if (target === "cancel") return;

      const res = await axios.post(
        "/snotes/executeScript/downloadDllDotNet",
        { fg_item: dllFolder, nm_file: file.nm_file },
        {
          headers: {
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
          responseType: "blob",
        }
      );

      await writeBlob(target, file.nm_file, new Blob([res.data]));
    } catch (err: any) {
      await showDialog(
        "error",
        downloadErrorMessage("파일 다운로드를 실패했습니다.", err)
      );
    }
  };

  const handleDelete = async (file: any) => {
    if (locked) {
      await showDialog("alter", LOCKED_MESSAGE);
      return;
    }
    try {
      const confirm = await showDialog("confirm", "파일을 삭제하시겠습니까?");
      if (!confirm) return;

      const res = await axios.post(
        "/snotes/executeScript/removeDllDotNet",
        { no_issue, fg_item: dllFolder, nm_file: file.nm_file },
        {
          headers: {
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
        }
      );

      const result = res.data?.result;
      if (!result || result.if_status !== "S") {
        throw new Error(result?.message || "delete failed");
      }

      await onDeleted?.();
      await showDialog("success", "파일이 삭제되었습니다.");
    } catch (err: any) {
      await showDialog(
        "error",
        err?.message
          ? `파일 삭제를 실패했습니다.\n${err.message}`
          : "파일 삭제를 실패했습니다."
      );
    }
  };

  // 윈폼 btnFileHistory(Show File Infomation) → POP_09: 이슈에 올라간 파일(dll, 첨부) 이력 조회/다운로드
  const handleHistory = async () => {
    if (!no_issue) {
      await showDialog("alter", "저장 후 첨부이력을 볼 수 있습니다.");
      return;
    }
    await showPopupModal({
      title: "File Info",
      width: "850px",
      height: "560px",
      okayBtn: false,
      closeBtn: true,
      closeBtnText: "닫기",
      children: (
        <SN_SFA_ISSUE_REG_002_POP_006
          no_issue={no_issue}
          dll_folder={dllFolder}
        />
      ),
    });
  };

  const uploadFiles = async (fileList: FileList) => {
    if (!no_issue) {
      await showDialog("alter", "저장 후 DLL을 첨부할 수 있습니다.");
      return;
    }
    // 드래그앤드롭·파일선택 모두 여기를 거친다
    if (locked) {
      await showDialog("alter", LOCKED_MESSAGE);
      return;
    }
    if (!fg_item) {
      await showDialog(
        "alter",
        "제품구분(fg_item)이 없어 DLL을 첨부할 수 없습니다."
      );
      return;
    }

    try {
      const formData = new FormData();
      for (let i = 0; i < fileList.length; i++) {
        formData.append("files", fileList[i]);
      }
      formData.append("no_issue", no_issue);
      formData.append("fg_item", dllFolder);

      const res = await axios.post(
        "/snotes/executeScript/uploadDllDotNet",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-TENANT-ID": global.getState().userInfo.tenant_id || "DEFAULT",
          },
        }
      );

      const result = res.data?.result;
      if (!result || result.if_status !== "S") {
        throw new Error(result?.message || "upload failed");
      }

      await onUploaded?.();
      await showDialog("success", "파일이 저장되었습니다.");
    } catch (err: any) {
      await showDialog(
        "error",
        err?.message
          ? `파일 업로드를 실패했습니다.\n${err.message}`
          : "파일 업로드를 실패했습니다."
      );
    }
  };

  return (
    <div
      style={{
        border: "1px solid #babfc7",
        padding: "5px",
        marginTop: 6,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{ flex: 1, minHeight: 25, textAlign: "center" }}
      >
        {!dllList || dllList.length === 0 ? (
          <p
            style={{
              color: "gray",
              fontSize: 14,
              lineHeight: "25px",
              margin: 0,
            }}
          >
            DLL/XML 파일을 마우스로 끌어 오세요
          </p>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              justifyContent: "flex-start",
            }}
          >
            {dllList.map((file: any, idx: number) => (
              <Chip
                key={`${file.nm_file}-${idx}`}
                label={file.nm_file}
                variant="outlined"
                onClick={() => handleDownload(file)}
                // 완료·취소 이슈는 X(삭제) 아이콘 자체를 숨긴다
                onDelete={locked ? undefined : () => handleDelete(file)}
                sx={{
                  height: "auto",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                  cursor: "pointer",
                  borderRadius: "3px",
                  "& .MuiChip-label": {
                    textDecoration: "underline",
                    paddingRight: "3px",
                    paddingLeft: "8px",
                  },
                  "& .MuiChip-deleteIcon": {
                    fontSize: "16px",
                    marginLeft: "4px",
                  },
                }}
              />
            ))}
          </div>
        )}
      </div>
      <SnButton text={"첨부이력"} onClick={handleHistory} />
      <SnButton
        text={"DLL 첨부"}
        disabled={locked}
        onClick={() => inputRef.current?.click()}
      />

      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".dll,.xml,.exe,.repx"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default React.memo(SN_SFA_ISSUE_REG_002_DLL_BOX);
